import React, { useState, useEffect } from "react";
  import { useNavigate } from "react-router-dom";
  import StudentSidebar from "../components/studentsidebar";
  import Navbar from "../components/studentnavbar";
  import { useSidebar } from '../components/SidebarContext';
  import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
  import { faEye } from '@fortawesome/free-solid-svg-icons';
  import api from "../services/api";

  const StudentMark = () => {
    const { isSidebarOpen } = useSidebar();
    const navigate = useNavigate();
    const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({
      testsCompleted: 0,
      averageScore: 0,
      highestScore: 0,
    });
    const [recentTests, setRecentTests] = useState([]);

    const getStudentId = () => {
      console.log('Getting student ID...');
      let studentId = localStorage.getItem('id');
      if (!studentId) {
        const studentData = localStorage.getItem('student');
        if (studentData) {
          try {
            const student = JSON.parse(studentData);
            studentId = student._id || student.id;
          } catch (parseError) {
            console.error('Error parsing student data:', parseError);
          }
        }
      }
      console.log('Student ID found:', studentId);
      return studentId;
    };

    useEffect(() => {
      const fetchTestResults = async () => {
        try {
          setLoading(true);
          setError('');
          const studentId = getStudentId();
          if (!studentId) {
            throw new Error('Student ID not found. Please login again.');
          }
          const response = await api.get(`/api/results/student/${studentId}`);
          if (response.data && response.data.length > 0) {
            const testsCompleted = response.data.length;
            const scores = response.data.map(test => test.totalCorrect || 0);
            const averageScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
            const highestScore = scores.length > 0 ? Math.max(...scores) : 0;
            setStats({ testsCompleted, averageScore, highestScore });
            const formattedTests = response.data.slice(0, 5).map(test => ({
              id: test._id,
              name: test.testName || 'Unnamed Test',
              date: test.attemptedAt || test.completedAt,
              score: test.totalCorrect || 0,
              total: test.totalQuestions || 0,
              resultId: test._id,
              overallPercentage: test.totalCorrect && test.totalQuestions ? 
                Math.round((test.totalCorrect / test.totalQuestions) * 100) : 0
            }));
            setRecentTests(formattedTests);
          }
          setLoading(false);
        } catch (err) {
          console.error('Error fetching test results:', err);
          setError(err.message || 'Failed to load test data. Please try again.');
          setLoading(false);
        }
      };
      fetchTestResults();
    }, []);

    const viewTestResult = (testId) => {
      console.log('Navigating to test result with ID:', testId);
      navigate(`/stored-result/${testId}`, { 
        state: { 
          from: '/studentmark-dashboard',
          isAdmin: false,
          userRole: 'student',
          studentName: localStorage.getItem('name') || 'Student'
        } 
      });
    };

    const navigateToAllResults = () => {
      navigate('/Result');
    };

    const styles = {
      container: {
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        minHeight: "100vh",
        background: "linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)",
      },
      sidebar: {
        width: sidebarWidth,
        borderRadius: '30px',
        height: '100vh',
        flexShrink: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 10,
        transition: 'width 0.3s ease',
        fontFamily: "'Roboto', sans-serif",
      },
      content: {
        flex: 1,
        padding: '15px',
        margin: '6rem 2rem 2rem 2rem',
        backgroundColor: 'transparent',
        transition: 'margin-left 0.3s ease',
        height: 'calc(100vh - 8rem)',
        borderTopLeftRadius: '1rem',
        borderTopRightRadius: '1rem',
        borderBottomLeftRadius: '1rem',
        borderBottomRightRadius: '1rem',
        maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      },
      cardStyle: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '10px',
        marginBottom: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      },
      cardTitleStyle: {
        fontSize: '16px',
        fontWeight: '600',
        marginBottom: '6px',
        color: '#552095',
        fontFamily: "'Roboto', sans-serif",
      },
      cardTextStyle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#1F2937',
        fontFamily: "'Roboto', sans-serif",
      },
      tableContainer: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        maxHeight: 'calc(100vh - 12rem)',
        height: 'auto',
      },
      table: {
        width: '100%',
        borderCollapse: 'collapse',
        border: 'none',
        fontSize: '12px',
      },
      headerRow: {
        background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
        position: 'sticky',
        top: 0,
        zIndex: 2,
      },
      th: {
        padding: '12px 10px',
        fontWeight: '500',
        textAlign: 'center',
        color: '#6B21A8',
        fontSize: '12px',
        borderBottom: '2px solid #e5e7eb',
        fontFamily: "'Roboto', sans-serif",
      },
      row: {
        borderRadius: '8px',
      },
      td: {
        padding: '10px 12px',
        verticalAlign: 'middle',
        textAlign: 'center',
        color: '#333',
        fontFamily: "'Roboto', sans-serif",
      },
      actionButton: {
        backgroundColor: 'transparent',
        color: '#cc8888',
        border: 'none',
        borderRadius: '2px',
        padding: '2px',
        cursor: 'pointer',
        transition: 'color 0.3s ease, transform 0.3s ease',
        fontFamily: "'Roboto', sans-serif",
      },
      viewAllButtonStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 16px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#6B21A8',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        cursor: 'pointer',
        borderRadius: '30px',
        border: '2px solid transparent',
        backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        transition: 'all 0.4s ease',
        fontFamily: "'Roboto', sans-serif",
      },
      notificationContainer: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        backgroundColor: '#fffffff',
        color: '#6B21A8',
        fontFamily: "'Roboto', sans-serif",
      },
    };

    return (
      <div style={styles.container}>
        <style>{`
          @keyframes slideInFromRight {
            0% {
              transform: translateX(100%);
              opacity: 0;
            }
            100% {
              transform: translateX(0);
              opacity: 1;
            }
          }
          @keyframes glow {
            0% { box-shadow: 0 0 5px rgba(168, 85, 247, 0.5); }
            50% { box-shadow: 0 0 15px rgba(168, 85, 247, 0.8); }
            100% { box-shadow: 0 0 5px rgba(168, 85, 247, 0.5); }
          }
          .custom-table {
            border: none !important;
          }
          .custom-table th,
          .custom-table td {
            border: none !important;
          }
          .custom-table tbody tr:nth-child(odd) {
            background-color: #FFFFFF;
          }
          .custom-table tbody tr:nth-child(even) {
            background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB);
          }
          .custom-table tbody tr:hover {
            background: linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5) !important;
          }
          .action-button:hover {
            color: #b36666 !important;
            transform: scale(1.1);
          }
          .table-container::-webkit-scrollbar {
            width: 12px;
            height: 12px;
          }
          .table-container::-webkit-scrollbar-track {
            background: #f8f9fa;
            border-radius: 6px;
          }
          .table-container::-webkit-scrollbar-thumb {
            background: #6c757d;
            border-radius: 6px;
            border: 2px solid #f8f9fa;
          }
          .table-container::-webkit-scrollbar-thumb:hover {
            background: #495057;
          }
          .table-container::-webkit-scrollbar-corner {
            background: #f8f9fa;
          }
          .table-container {
            scrollbar-width: thin;
            scrollbar-color: #6c757d #f8f9fa;
          }
          .view-all-btn:hover {
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }
          .custom-table td:nth-child(2) {
            text-align: left !important;
          }
          .student-mark-wrapper, .student-mark-wrapper * {
            font-family: 'Roboto', sans-serif !important;
          }
        `}</style>
        <div style={styles.sidebar}>
          <StudentSidebar />
        </div>
        <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'all 0.3s ease' }}>
          <Navbar />
          <div style={styles.content} className="student-mark-wrapper">
            {error && (
              <div style={styles.notificationContainer}>
                <span>{error}</span>
              </div>
            )}
            {loading ? (
              <div style={styles.notificationContainer}>
                <p>Loading...</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ flex: '1 1 200px', ...styles.cardStyle }}>
                    <div style={styles.cardTitleStyle}>Tests Completed</div>
                    <div style={styles.cardTextStyle}>{stats.testsCompleted}</div>
                  </div>
                  <div style={{ flex: '1 1 200px', ...styles.cardStyle }}>
                    <div style={styles.cardTitleStyle}>Average Score</div>
                    <div style={styles.cardTextStyle}>{stats.averageScore}</div>
                  </div>
                  <div style={{ flex: '1 1 200px', ...styles.cardStyle }}>
                    <div style={styles.cardTitleStyle}>Highest Score</div>
                    <div style={styles.cardTextStyle}>{stats.highestScore}</div>
                  </div>
                </div>
                <div style={{ ...styles.cardStyle, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    padding: '8px',
                    borderBottom: '1px solid #dee2e6'
                  }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#552095', margin: 0 }}>
                      Recent Test Results
                    </h4>
                    <button 
                      className="view-all-btn"
                      style={styles.viewAllButtonStyle}
                      onClick={navigateToAllResults}
                    >
                      View All
                    </button>
                  </div>
                  <div style={styles.tableContainer} className="table-container">
                    {recentTests.length === 0 ? (
                      <div style={styles.notificationContainer}>
                        <p>No test results found. Take a test to see your results here.</p>
                      </div>
                    ) : (
                      <table className="custom-table" style={styles.table}>
                        <thead style={styles.headerRow}>
                          <tr>
                            <th style={styles.th}>Sr.</th>
                            <th style={styles.th}>Test Name</th>
                            <th style={styles.th}>Score (%)</th>
                            <th style={styles.th}>Raw Score</th>
                            <th style={styles.th}>Total Questions</th>
                            <th style={styles.th}>Date Attempted</th>
                            <th style={styles.th}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentTests.map((test, index) => (
                            <tr
                              key={test.id || index}
                              style={styles.row}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = index % 2 === 0 ? '#FFFFFF' : 'linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB)';
                              }}
                            >
                              <td style={styles.td}>{index + 1}</td>
                              <td style={styles.td}>{test.name}</td>
                              <td style={styles.td}>{test.overallPercentage}%</td>
                              <td style={styles.td}>{test.score}/{test.total}</td>
                              <td style={styles.td}>{test.total}</td>
                              <td style={styles.td}>
                                {new Date(test.date).toLocaleDateString("en-GB", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </td>
                              <td style={styles.td}>
                                <button
                                  className="action-button"
                                  style={styles.actionButton}
                                  onClick={() => viewTestResult(test.id)}
                                  title="View test result"
                                >
                                  <FontAwesomeIcon icon={faEye} style={{ fontSize: '17px' }} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  export default StudentMark;